"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.createNetSuiteUtils = void 0;
const netSuiteErrorFormat_1 = require("./netSuiteErrorFormat");
const billingAccountAddressUtils_1 = require("./billingAccountAddressUtils");
const createNetSuiteUtils = ({ client }) => {
    // Helper function to parse NetSuite date format (e.g., "1-Feb-2026") to ISO format (YYYY-MM-DD)
    // without timezone conversion issues
    const parseNetSuiteDateToISO = (netsuiteDate) => {
        const [day, month, year] = netsuiteDate.split('-');
        const monthMap = {
            'Jan': '01', 'Feb': '02', 'Mar': '03', 'Apr': '04',
            'May': '05', 'Jun': '06', 'Jul': '07', 'Aug': '08',
            'Sep': '09', 'Oct': '10', 'Nov': '11', 'Dec': '12'
        };
        const monthNumber = monthMap[month];
        if (!monthNumber) {
            throw new Error(`Invalid month in NetSuite date: ${netsuiteDate}`);
        }
        return `${year}-${monthNumber}-${day.padStart(2, '0')}`;
    };
    // Helper function to convert date to YYYY-MM-DD format for NetSuite API
    // Handles Date objects, ISO strings with time, NetSuite format (D-Mon-YYYY), and already-formatted YYYY-MM-DD
    const convertToYYYYMMDD = (date) => {
        if (date instanceof Date) {
            const year = date.getUTCFullYear();
            const month = String(date.getUTCMonth() + 1).padStart(2, '0');
            const day = String(date.getUTCDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        }
        let dateStr = date;
        if (dateStr.includes('T')) {
            dateStr = dateStr.split('T')[0];
        }
        // If date is in NetSuite format (D-Mon-YYYY), convert to YYYY-MM-DD
        if (/^\d{1,2}-[A-Za-z]{3}-\d{4}$/.test(dateStr)) {
            const [day, month, year] = dateStr.split('-');
            const monthMap = {
                'Jan': '01', 'Feb': '02', 'Mar': '03', 'Apr': '04',
                'May': '05', 'Jun': '06', 'Jul': '07', 'Aug': '08',
                'Sep': '09', 'Oct': '10', 'Nov': '11', 'Dec': '12'
            };
            const monthNumber = monthMap[month];
            if (monthNumber) {
                return `${year}-${monthNumber}-${day.padStart(2, '0')}`;
            }
        }
        return dateStr;
    };
    // Helper function to convert ISO date format (YYYY-MM-DD) to NetSuite format (D-Mon-YYYY)
    // This prevents timezone issues when sending dates to NetSuite API
    const convertISOToNetSuiteFormat = (isoDate) => {
        // If already in NetSuite format, return as-is
        if (/^\d{1,2}-[A-Za-z]{3}-\d{4}$/.test(isoDate)) {
            return isoDate;
        }
        // If ISO format (YYYY-MM-DD), convert to NetSuite format
        if (/^\d{4}-\d{2}-\d{2}$/.test(isoDate)) {
            const [year, month, day] = isoDate.split('-');
            const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
            const monthName = monthNames[parseInt(month) - 1];
            return `${parseInt(day)}-${monthName}-${year}`;
        }
        // If ISO with time (YYYY-MM-DDTHH:mm:ss.sssZ), extract date part first
        if (isoDate.includes('T')) {
            const datePart = isoDate.split('T')[0];
            return convertISOToNetSuiteFormat(datePart);
        }
        // Unknown format, return as-is
        return isoDate;
    };
    // Smart date converter for API calls - ensures dates are in NetSuite format
    // Accepts: NetSuite format, ISO format, or Date object
    // Returns: NetSuite format string
    const ensureNetSuiteDateFormat = (date) => {
        // If Date object, convert to ISO first, then to NetSuite format
        if (date instanceof Date) {
            const year = date.getUTCFullYear();
            const month = String(date.getUTCMonth() + 1).padStart(2, '0');
            const day = String(date.getUTCDate()).padStart(2, '0');
            const iso = `${year}-${month}-${day}`;
            return convertISOToNetSuiteFormat(iso);
        }
        // If string, check format and convert if needed
        return convertISOToNetSuiteFormat(date);
    };
    const fetchData = async (endpoint) => {
        try {
            console.log(`[NetSuiteUtils] fetchData GET ${endpoint}`);
            const data = await client.get(endpoint);
            console.log(`[NetSuiteUtils] fetchData GET ${endpoint} -> ok`);
            return data;
        }
        catch (error) {
            console.error(`[NetSuiteUtils] fetchData GET ${endpoint} failed: ${(0, netSuiteErrorFormat_1.describeNetSuiteError)(error)}`);
            throw error;
        }
    };
    const postData = async (endpoint, data) => {
        try {
            console.log(`[NetSuiteUtils] postData POST ${endpoint}`);
            const response = await client.post(endpoint, data);
            console.log(`[NetSuiteUtils] postData POST ${endpoint} -> ok`);
            return response;
        }
        catch (error) {
            console.error(`[NetSuiteUtils] postData POST ${endpoint} failed: ${(0, netSuiteErrorFormat_1.describeNetSuiteError)(error)}`);
            throw error;
        }
    };
    const patchData = async (endpoint, data) => {
        try {
            console.log(`[NetSuiteUtils] patchData PATCH ${endpoint}`);
            const response = await client.patch(endpoint, data);
            console.log(`[NetSuiteUtils] patchData PATCH ${endpoint} -> ok`);
            return response;
        }
        catch (error) {
            console.error(`[NetSuiteUtils] patchData PATCH ${endpoint} failed: ${(0, netSuiteErrorFormat_1.describeNetSuiteError)(error)}`);
            throw error;
        }
    };
    const deleteData = async (endpoint) => {
        try {
            console.log(`[NetSuiteUtils] deleteData DELETE ${endpoint}`);
            const response = await client.delete(endpoint);
            console.log(`[NetSuiteUtils] deleteData DELETE ${endpoint} -> ok`);
            return response;
        }
        catch (error) {
            console.error(`[NetSuiteUtils] deleteData DELETE ${endpoint} failed: ${(0, netSuiteErrorFormat_1.describeNetSuiteError)(error)}`);
            throw error;
        }
    };
    const executeSuiteQL = async (query) => {
        try {
            const result = await client.suiteql(query);
            return result;
        }
        catch (error) {
            console.error("Error executing SuiteQL:", error);
            throw error;
        }
    };
    const getCustomer = async (customerId) => {
        return fetchData(`/services/rest/record/v1/customer/${customerId}`);
    };
    const getClass = async (classId) => {
        return fetchData(`/services/rest/record/v1/classification/${classId}`);
    };
    const getSubscriptionPlan = async (planId) => {
        return fetchData(`/services/rest/record/v1/subscriptionPlan/${planId}`);
    };
    const getSubscriptionDetails = async (subscriptionId) => {
        return fetchData(`/services/rest/record/v1/subscription/${subscriptionId}`);
    };
    const getPriceIntervals = async (subscriptionId) => {
        return fetchData(`/services/rest/record/v1/subscription/${subscriptionId}/priceInterval`);
    };
    const fetchPriceIntervals = async (subscriptionId) => {
        try {
            const priceIntervals = await client.get(`/services/rest/record/v1/subscription/${subscriptionId}/priceinterval`);
            return priceIntervals;
        }
        catch (error) {
            console.error("Error fetching subscription lines:", error);
        }
    };
    const getPriceIntervalDetails = async (url) => {
        return fetchData(url);
    };
    const getMaxSubscriptionLineNumber = async (subscriptionId) => {
        return fetchData(`/services/rest/record/v1/subscription/${subscriptionId}/subscriptionLine`);
    };
    const createNewPricePlan = async (payload) => {
        const url = '/services/rest/record/v1/pricePlan';
        const response = client.post(url, payload);
        return response;
    };
    const updateSubscription = async (subscriptionId, payload) => {
        try {
            const url = `/services/rest/record/v1/subscription/${subscriptionId}`;
            const response = await client.patch(url, payload);
            if (response.status === 200) {
                console.log(`Subscription ${subscriptionId} updated successfully.`);
                return true;
            }
            else if (response.status === 204) {
                console.log(`Subscription ${subscriptionId} updated successfully (No Content).`);
                return true;
            }
            else {
                console.log("Failed to update subscription:", response);
                return false;
            }
        }
        catch (error) {
            console.error("Error updating subscription:", error);
            return false;
        }
    };
    const fetchNonSaleInventoryItems = async (itemId) => {
        return await fetchData(`/services/rest/record/v1/noninventorysaleitem/${itemId}`);
    };
    const getCustomerAddresses = async (customerId) => {
        const resp = await fetchData(`/services/rest/record/v1/customer/${customerId}/addressbook`);
        // Process addresses sequentially to avoid NetSuite concurrency limits
        const addresses = [];
        for (const item of resp.items) {
            const addressEndpoint = item.links
                .find((link) => link.rel === "self")
                ?.href?.split(".com")?.[1];
            const address = addressEndpoint ? await fetchData(addressEndpoint) : {};
            addresses.push(address);
        }
        const addrTexts = addresses.map((addr) => ({
            id: addr.id,
            addrText: addr.addressBookAddress_text,
            defaultBilling: addr.defaultBilling,
            defaultShipping: addr.defaultShipping,
        }));
        return addrTexts;
    };
    const getCustomerSubsidiaries = async (customerId) => {
        const query = `SELECT * FROM customerSubsidiaryRelationship WHERE entity = ${customerId}`;
        const resp = await executeSuiteQL(query);
        return resp.items;
    };
    const addSubsidiaryToCustomer = async (customerId, subsidiaryId) => {
        const payload = {
            entity: { id: customerId },
            subsidiary: { id: subsidiaryId },
        };
        try {
            const response = await client.post("/services/rest/record/v1/customerSubsidiaryRelationship", payload);
            if (response.status === 204) {
                console.log("Subsidiary added to customer successfully.");
            }
            else {
                console.log("Failed to add subsidiary to customer:", response);
            }
        }
        catch (e) {
            const error = e;
            throw new Error("Error adding subsidiary to customer:" + error.message);
        }
    };
    const addAddressToCustomer = async (customerId, payload) => {
        const currentAddresses = await getCustomerAddresses(customerId);
        console.log("Current Addresses:", JSON.stringify(currentAddresses, null, 2));
        const createAddressResp = await client.patch(`/services/rest/record/v1/customer/${customerId}`, {
            addressBook: {
                items: [
                    {
                        addressBookAddress: {
                            ...payload,
                        },
                    },
                ],
            },
        });
        if (createAddressResp.status === 204) {
            console.log("Address added to customer successfully.");
            const newAddresses = await getCustomerAddresses(customerId);
            console.log("New Addresses:", JSON.stringify(newAddresses, null, 2));
            const newAddress = newAddresses.find((addr) => !currentAddresses.some((cAddr) => cAddr.id === addr.id));
            console.log("New Address:", newAddress);
            return newAddress?.id || null;
        }
        console.log("Failed to add address to customer:", createAddressResp);
        return null;
    };
    const createBillingAccount = async (customerId, scheduleId, subsidiaryId, classId, departmentId, currencyId, effectiveDate, invoiceFormId) => {
        // Convert date to YYYY-MM-DD format for NetSuite API
        const startdateFormatted = convertToYYYYMMDD(effectiveDate);
        console.log(`[createBillingAccount] effectiveDate (input): ${effectiveDate} → startdate (YYYY-MM-DD): ${startdateFormatted}`);
        const payload = {
            customer: customerId,
            billingschedule: scheduleId,
            subsidiary: subsidiaryId,
            class: classId,
            department: departmentId,
            startdate: startdateFormatted,
            currency: {
                id: currencyId,
            },
            invoiceForm: {
                id: invoiceFormId,
            }
        };
        console.log("Payload:", payload);
        try {
            const response = await client.post("/services/rest/record/v1/billingaccount", payload);
            if (response.status === 204) {
                console.log("Billing account created successfully:", response.id);
                return response.id;
            }
            else {
                console.log("Failed to create billing account:", response);
                return null;
            }
        }
        catch (error) {
            console.error("Error creating billing account:", error);
            return null;
        }
    };
    const getBillingAccount = async (billingAccountId) => {
        return fetchData(`/services/rest/record/v1/billingaccount/${billingAccountId}`);
    };
    const updateBillingAccountAddress = async (billingAccountId, options) => {
        const payload = {};
        if (options.shipAddressList != null && options.shipAddressList !== "") {
            payload.shipAddressList = options.shipAddressList;
        }
        if (options.billAddressList != null && options.billAddressList !== "") {
            payload.billAddressList = options.billAddressList;
        }
        if (options.shipAddress != null && options.shipAddress !== "") {
            payload.shipAddress = options.shipAddress;
        }
        if (options.billAddress != null && options.billAddress !== "") {
            payload.billAddress = options.billAddress;
        }
        if (Object.keys(payload).length === 0) {
            throw new Error("updateBillingAccountAddress: no address fields provided");
        }
        const response = await client.patch(`/services/rest/record/v1/billingaccount/${billingAccountId}`, payload);
        if (response.status !== 204) {
            throw new Error(`Failed to update billing account ${billingAccountId} addresses: status ${response.status}`);
        }
        console.log(`[updateBillingAccountAddress] Updated billing account ${billingAccountId}: ${JSON.stringify(payload)}`);
    };
    const findCustomerAddressBookIdByText = async (customerId, targetText) => {
        const normalizedTarget = (0, billingAccountAddressUtils_1.normalizeAddressTextForCompare)(targetText);
        if (!normalizedTarget) {
            return null;
        }
        const addresses = await getCustomerAddresses(customerId);
        for (const addr of addresses) {
            if ((0, billingAccountAddressUtils_1.normalizeAddressTextForCompare)(addr.addrText) === normalizedTarget) {
                return addr.id;
            }
        }
        return null;
    };
    const resolveAddressListIdForSide = async (customerId, side, snapshot) => {
        const listIdStr = side === "ship" ? snapshot.shipAddressListId : snapshot.billAddressListId;
        const text = side === "ship" ? snapshot.shipAddressText : snapshot.billAddressText;
        if (listIdStr) {
            return { listId: Number(listIdStr), method: "LIST_ID" };
        }
        if (text) {
            const matched = await findCustomerAddressBookIdByText(customerId, text);
            if (matched) {
                return { listId: matched, method: "ADDRESSBOOK_MATCH" };
            }
            const created = await addAddressToCustomer(customerId, { addrText: text });
            if (created) {
                return { listId: created, method: "ADDRESSBOOK_CREATED" };
            }
        }
        return { listId: null, method: "UNCHANGED" };
    };
    const verifyBillingAccountAddressSide = (oldSnap, newSnap, side) => {
        if (side === "ship") {
            return {
                listOk: (0, billingAccountAddressUtils_1.billingAccountAddressListIdsMatch)(oldSnap.shipAddressListId, newSnap.shipAddressListId),
                textOk: (0, billingAccountAddressUtils_1.billingAccountAddressTextMatches)(oldSnap.shipAddressText, newSnap.shipAddressText),
            };
        }
        return {
            listOk: (0, billingAccountAddressUtils_1.billingAccountAddressListIdsMatch)(oldSnap.billAddressListId, newSnap.billAddressListId),
            textOk: (0, billingAccountAddressUtils_1.billingAccountAddressTextMatches)(oldSnap.billAddressText, newSnap.billAddressText),
        };
    };
    const applyBillingAccountAddressFallback = async (newBillingAccountId, customerId, side, oldSnap) => {
        const text = side === "ship" ? oldSnap.shipAddressText : oldSnap.billAddressText;
        const listIdStr = side === "ship" ? oldSnap.shipAddressListId : oldSnap.billAddressListId;
        // 1) Re-apply old list id if present
        if (listIdStr) {
            try {
                await updateBillingAccountAddress(newBillingAccountId, {
                    ...(side === "ship"
                        ? { shipAddressList: listIdStr }
                        : { billAddressList: listIdStr }),
                });
                const ba = await getBillingAccount(newBillingAccountId);
                const snap = (0, billingAccountAddressUtils_1.extractBillingAccountAddressFields)(ba);
                const v = verifyBillingAccountAddressSide(oldSnap, snap, side);
                if (v.listOk && v.textOk) {
                    return "LIST_ID";
                }
            }
            catch (e) {
                console.warn(`[copyBillingAccountAddresses] List id re-apply failed for ${side}:`, (0, netSuiteErrorFormat_1.describeNetSuiteError)(e) || e);
            }
        }
        // 2) Match or create customer address book row from old display text
        if (text) {
            let bookId = await findCustomerAddressBookIdByText(customerId, text);
            let method = "ADDRESSBOOK_MATCH";
            if (!bookId) {
                bookId = await addAddressToCustomer(customerId, { addrText: text });
                method = "ADDRESSBOOK_CREATED";
            }
            if (bookId) {
                try {
                    await updateBillingAccountAddress(newBillingAccountId, {
                        ...(side === "ship"
                            ? { shipAddressList: bookId }
                            : { billAddressList: bookId }),
                    });
                    const ba = await getBillingAccount(newBillingAccountId);
                    const snap = (0, billingAccountAddressUtils_1.extractBillingAccountAddressFields)(ba);
                    if (verifyBillingAccountAddressSide(oldSnap, snap, side).textOk) {
                        return method;
                    }
                }
                catch (e) {
                    console.warn(`[copyBillingAccountAddresses] Address book list apply failed for ${side}:`, (0, netSuiteErrorFormat_1.describeNetSuiteError)(e) || e);
                }
            }
            // 3) Invoice-style free-text PATCH (may not be supported on all BA forms)
            try {
                await updateBillingAccountAddress(newBillingAccountId, {
                    ...(side === "ship" ? { shipAddress: text } : { billAddress: text }),
                });
                const ba = await getBillingAccount(newBillingAccountId);
                const snap = (0, billingAccountAddressUtils_1.extractBillingAccountAddressFields)(ba);
                if (verifyBillingAccountAddressSide(oldSnap, snap, side).textOk) {
                    return "TEXT_PATCH";
                }
            }
            catch (e) {
                console.warn(`[copyBillingAccountAddresses] Text PATCH failed for ${side}:`, (0, netSuiteErrorFormat_1.describeNetSuiteError)(e) || e);
            }
        }
        return "UNCHANGED";
    };
    const applyCustomerDefaultBillingAccountAddresses = async (newBillingAccountId, customerId) => {
        const addresses = await getCustomerAddresses(customerId);
        const defaultBilling = addresses.find((addr) => addr.defaultBilling)?.id;
        const defaultShipping = addresses.find((addr) => addr.defaultShipping)?.id;
        if (!defaultBilling || !defaultShipping) {
            if (addresses.length > 0) {
                await updateBillingAccountAddress(newBillingAccountId, {
                    shipAddressList: addresses[0].id,
                    billAddressList: addresses[0].id,
                });
            }
            return;
        }
        await updateBillingAccountAddress(newBillingAccountId, {
            shipAddressList: defaultShipping,
            billAddressList: defaultBilling,
        });
    };
    /**
     * Copy ship/bill addresses from old billing account to new: list ids first, verify via GET,
     * then address-book or text PATCH fallback per side.
     */
    const copyBillingAccountAddressesFromOld = async (oldBillingAccountId, newBillingAccountId, customerId) => {
        const oldBa = await getBillingAccount(oldBillingAccountId);
        const oldSnap = (0, billingAccountAddressUtils_1.extractBillingAccountAddressFields)(oldBa);
        console.log(`[copyBillingAccountAddresses] Old BA ${oldBillingAccountId}: shipList=${oldSnap.shipAddressListId} billList=${oldSnap.billAddressListId}`);
        const shipResolved = await resolveAddressListIdForSide(customerId, "ship", oldSnap);
        const billResolved = await resolveAddressListIdForSide(customerId, "bill", oldSnap);
        const patchPayload = {};
        if (shipResolved.listId != null) {
            patchPayload.shipAddressList = shipResolved.listId;
        }
        if (billResolved.listId != null) {
            patchPayload.billAddressList = billResolved.listId;
        }
        let shipMethod = shipResolved.method;
        let billMethod = billResolved.method;
        if (patchPayload.shipAddressList != null || patchPayload.billAddressList != null) {
            await updateBillingAccountAddress(newBillingAccountId, patchPayload);
        }
        else {
            await applyCustomerDefaultBillingAccountAddresses(newBillingAccountId, customerId);
            shipMethod = "CUSTOMER_DEFAULT";
            billMethod = "CUSTOMER_DEFAULT";
        }
        let newBa = await getBillingAccount(newBillingAccountId);
        let newSnap = (0, billingAccountAddressUtils_1.extractBillingAccountAddressFields)(newBa);
        const shipVerify = verifyBillingAccountAddressSide(oldSnap, newSnap, "ship");
        if (!shipVerify.listOk || !shipVerify.textOk) {
            const fallback = await applyBillingAccountAddressFallback(newBillingAccountId, customerId, "ship", oldSnap);
            if (fallback !== "UNCHANGED") {
                shipMethod = fallback;
            }
            newBa = await getBillingAccount(newBillingAccountId);
            newSnap = (0, billingAccountAddressUtils_1.extractBillingAccountAddressFields)(newBa);
        }
        const billVerify = verifyBillingAccountAddressSide(oldSnap, newSnap, "bill");
        if (!billVerify.listOk || !billVerify.textOk) {
            const fallback = await applyBillingAccountAddressFallback(newBillingAccountId, customerId, "bill", oldSnap);
            if (fallback !== "UNCHANGED") {
                billMethod = fallback;
            }
            newBa = await getBillingAccount(newBillingAccountId);
            newSnap = (0, billingAccountAddressUtils_1.extractBillingAccountAddressFields)(newBa);
        }
        console.log(`[copyBillingAccountAddresses] New BA ${newBillingAccountId}: shipList=${newSnap.shipAddressListId} (${shipMethod}) billList=${newSnap.billAddressListId} (${billMethod})`);
        return { shipMethod, billMethod, oldSnapshot: oldSnap, newSnapshot: newSnap };
    };
    const createPricePlan = async (currencyId, value) => {
        const payload = {
            currency: { id: currencyId },
            pricePlanType: { id: "2" }, // constant for tieredPricing
            priceTiers: {
                items: [
                    {
                        fromVal: 0,
                        pricingOption: { id: "-102" }, // constant for fixed value
                        value: value,
                    },
                ],
            },
        };
        try {
            const response = await client.post("/services/rest/record/v1/pricePlan", payload);
            if (response.status === 204) {
                console.log("Price plan created successfully:", response.id);
                return response.id;
            }
            else {
                console.log("Failed to create price plan:", response);
                return null;
            }
        }
        catch (error) {
            console.error("Error creating price plan:", error);
            return null;
        }
    };
    const createSubscription = async (subName, customerId, billingAccountId, initialTerm, startDate, endDate, enduserId, resellerId, 
    //  salesRepId: string,
    subscriptionPlanId, subscriptionLine, priceInterval, renewalOptions, otherRefNum) => {
        // Convert dates to YYYY-MM-DD format for NetSuite API
        const startDateFormatted = convertToYYYYMMDD(startDate);
        const endDateFormatted = convertToYYYYMMDD(endDate);
        console.log(`[createSubscription] Converting dates to YYYY-MM-DD format:`);
        console.log(`  startDate: ${startDate} → ${startDateFormatted}`);
        console.log(`  endDate: ${endDate} → ${endDateFormatted}`);
        const payload = {
            name: subName,
            customer: {
                id: customerId,
            },
            billingAccount: {
                id: billingAccountId,
            },
            initialTerm: initialTerm,
            startDate: startDateFormatted,
            endDate: endDateFormatted,
            custrecord_subs_end_user: {
                id: enduserId,
            },
            custrecord_subs_reseller: {
                id: resellerId,
            },
            // salesRep: {
            //   id: salesRepId,
            // },
            subscriptionPlan: {
                id: subscriptionPlanId,
            },
            subscriptionline: {
                items: subscriptionLine.map((line, i) => ({
                    linenumber: line.linenumber,
                    item: line.item,
                    revrecoption: line.revrecoption,
                    billingmode: line.billingmode,
                    subscriptionlinetype: line.subscriptionlinetype,
                    isIncluded: line.isIncluded,
                })),
            },
            priceinterval: {
                items: priceInterval.map((interval, i) => ({
                    linenumber: interval.linenumber,
                    subscriptionplanlinenumber: interval.subscriptionplanlinenumber,
                    item: interval.item,
                    startDate: convertToYYYYMMDD(interval.startDate),
                    endDate: interval.endDate ? convertToYYYYMMDD(interval.endDate) : interval.endDate,
                    chargeFrequency: interval.chargeFrequency,
                    repeatEvery: interval.repeatEvery,
                    quantity: interval.quantity,
                    pricePlan: interval.pricePlan,
                })),
            },
            autoRenewal: renewalOptions.autoRenewal,
            defaultRenewalMethod: {
                id: renewalOptions.defaultRenewalMethod,
            },
            defaultRenewalTerm: {
                id: renewalOptions.defaultRenewalTerm,
            },
            defaultRenewalPlan: {
                id: renewalOptions.defaultRenewalPlan,
            },
            defaultRenewalTranType: "",
            advanceRenewalPeriodNumber: renewalOptions.advanceRenewalPeriodNumber,
            advanceRenewalPeriodUnit: {
                id: renewalOptions.advanceRenewalPeriodUnit,
            },
            custrecord_customer_po: otherRefNum,
        };
        console.log("Payload:", payload);
        console.log("JSON Payload:", JSON.stringify(payload, null, 2));
        try {
            const response = await client.post("/services/rest/record/v1/subscription", payload);
            if (response.status === 204) {
                console.log("Subscription created successfully:", response.id);
                return response.id;
            }
            else {
                console.log("Failed to create subscription:", response);
                return null;
            }
        }
        catch (error) {
            console.error("Error creating subscription:", error);
            return null;
        }
    };
    const turnOffAutoRenewal = async (subscriptionId) => {
        const payload = {
            autoRenewal: false,
        };
        try {
            const response = await client.patch(`/services/rest/record/v1/subscription/${subscriptionId}`, payload);
            if (response.status === 204) {
                console.log("Auto-renewal turned off successfully.");
            }
            else {
                console.log("Failed to turn off auto-renewal:", response);
            }
        }
        catch (error) {
            console.error("Error turning off auto-renewal:", error);
        }
    };
    const getSubscriptionPlanLines = async (subscriptionPlanId) => {
        try {
            const response = (await fetchData(`/services/rest/record/v1/subscriptionPlan/${subscriptionPlanId}/member`));
            console.log("Subscription Plan Lines Response:", JSON.stringify(response, null, 2));
            // Process subscription plan lines sequentially to avoid NetSuite concurrency limits
            const lines = [];
            for (const item of response.items) {
                const itemUrl = item.links
                    .find((link) => link.rel === "self")
                    ?.href?.split(".com")[1] || "";
                const itemData = await fetchData(itemUrl);
                lines.push(itemData);
            }
            console.log("Subscription Plan Lines:", JSON.stringify(lines, null, 2));
            return lines;
        }
        catch (error) {
            console.error("Error fetching subscription plan items:", error);
            return null;
        }
    };
    const getSubscriptionLines = async (subscriptionId) => {
        try {
            const lineLinks = await fetchData(`/services/rest/record/v1/subscription/${subscriptionId}/subscriptionline`);
            // Process subscription lines sequentially to avoid NetSuite concurrency limits
            const lines = [];
            for (const item of lineLinks.items) {
                const itemUrl = item.links
                    .find((link) => link.rel === "self")
                    ?.href?.split(".com")[1] || "";
                console.log(itemUrl);
                const line = await fetchData(itemUrl);
                lines.push(line);
            }
            return lines;
        }
        catch (error) {
            console.error("Error fetching subscription lines:", error);
        }
    };
    const getSubscriptionLinesNew = async (subscriptionId, options) => {
        const eligibleStatuses = new Set(options?.statusRefNames?.length ? options.statusRefNames : ["Active"]);
        try {
            const lineLinks = await fetchData(`/services/rest/record/v1/subscription/${subscriptionId}/subscriptionline`);
            // Process subscription lines sequentially to avoid NetSuite concurrency limits
            const lines = [];
            for (const item of lineLinks.items) {
                try {
                    const itemUrl = item.links.find((link) => link.rel === "self")?.href?.split(".com")[1] || "";
                    const itemId = parseInt(item.links[0].href.split("/").pop() || "");
                    const line = await fetchData(itemUrl);
                    const statusRefName = line?.status?.refName;
                    if (statusRefName &&
                        eligibleStatuses.has(statusRefName) &&
                        !line.terminationDate) {
                        console.log(`Item ${itemId} is ${statusRefName} and does not have a termination date`);
                        lines.push({ status: 'fulfilled', value: itemId });
                    }
                    else {
                        lines.push({ status: 'fulfilled', value: null });
                    }
                }
                catch (error) {
                    console.error(`Failed to fetch line for item ${item}:`, error);
                    lines.push({ status: 'rejected', value: null });
                }
            }
            // Handle all results and discard null values (which represent failed requests)
            const fulfilledLines = lines
                .map((result) => result.status === 'fulfilled' ? result.value : null) // Map to value or null
                .filter((line) => line !== null) // Filter out null values
                .map((line) => line); // Cast to number
            console.log("Fulfilled Lines:", fulfilledLines);
            return fulfilledLines;
        }
        catch (error) {
            console.error("Error fetching subscription lines:", error);
        }
    };
    const updateSubscriptionLineStatus = async (subscriptionId, lineNumber, statusId) => {
        const endpoint = `/services/rest/record/v1/subscription/${subscriptionId}/subscriptionline/${lineNumber}`;
        const payload = { status: { id: statusId } };
        try {
            const response = await client.patch(endpoint, payload);
            if (response.status === 204) {
                console.log(`Subscription line ${lineNumber} updated to status ${statusId} successfully.`);
            }
            else {
                console.log(`Failed to update subscription line ${lineNumber} to status ${statusId}:`, response);
            }
        }
        catch (error) {
            console.error(`Error updating subscription line ${lineNumber} to status ${statusId}:`, error);
        }
    };
    const createContractualDocument = async (subscriptionId, documentLink) => {
        const payload = {
            custrecord_sub_doc: { id: subscriptionId },
            name: documentLink,
        };
        try {
            const response = await client.post("/services/rest/record/v1/customrecord697", payload);
            if (response.status === 204) {
                console.log("Contractual document created successfully.");
                return response.id;
            }
            else {
                console.log("Failed to create contractual document:", response);
                return null;
            }
        }
        catch (error) {
            console.error("Error creating contractual document:", error);
            return null;
        }
    };
    const linkContractualDocument = async (subscriptionId, documentId) => {
        const payload = {
            custrecord_contract_docs: { id: documentId },
        };
        try {
            const response = await client.patch(`/services/rest/record/v1/subscription/${subscriptionId}`, payload);
            if (response.status === 204) {
                console.log("Contractual document linked successfully.");
            }
            else {
                console.log("Failed to link contractual document:", response);
            }
        }
        catch (error) {
            console.error("Error linking contractual document:", error);
        }
    };
    const activateSubscription = async (customerId, billingAccountId, subscriptionId, itemNumbers, startDate, refNumber) => {
        // Extract date part if startDate includes time component (e.g., "2026-03-01T00:00:00.000Z" -> "2026-03-01")
        // NetSuite API expects YYYY-MM-DD format for effectiveDate field
        let effectiveDate = startDate;
        if (startDate.includes('T')) {
            effectiveDate = startDate.split('T')[0];
        }
        // If date is in NetSuite format (D-Mon-YYYY), convert to YYYY-MM-DD
        if (/^\d{1,2}-[A-Za-z]{3}-\d{4}$/.test(effectiveDate)) {
            const [day, month, year] = effectiveDate.split('-');
            const monthMap = {
                'Jan': '01', 'Feb': '02', 'Mar': '03', 'Apr': '04',
                'May': '05', 'Jun': '06', 'Jul': '07', 'Aug': '08',
                'Sep': '09', 'Oct': '10', 'Nov': '11', 'Dec': '12'
            };
            const monthNumber = monthMap[month];
            if (monthNumber) {
                effectiveDate = `${year}-${monthNumber}-${day.padStart(2, '0')}`;
            }
        }
        console.log(`[activateSubscription] Start Date (input): ${startDate}`);
        console.log(`[activateSubscription] Start Date (YYYY-MM-DD format): ${effectiveDate}`);
        const payload = {
            action: { id: "ACTIVATE" },
            approvalStatus: { id: "APPROVED" },
            modificationType: { id: "NEW_CHURN" },
            customer: { id: customerId },
            billingAccount: { id: billingAccountId },
            subscription: { id: subscriptionId },
            subline: {
                items: itemNumbers.map((itemNumber) => ({
                    apply: true,
                    sequence: itemNumber,
                })),
            },
            effectiveDate: effectiveDate,
            custrecord_st_change_reason: {
                id: 1,
            },
            memo: refNumber,
        };
        try {
            const response = await client.post("/services/rest/record/v1/subscriptionChangeOrder", payload);
            if (response.status === 204) {
                console.log("Subscription activated successfully.");
            }
            else {
                console.log("Failed to activate subscription:", response);
            }
        }
        catch (error) {
            console.error("Error activating subscription:", error);
        }
    };
    const terminateSubscription = async (customerId, billingAccountId, subscriptionId, itemNumbers, endDate, memo) => {
        // memo is required: every TERMINATE SCO this util creates must carry an
        // identifying memo so downstream tools (e.g. subscription-transfer's Open
        // SCO gate) can attribute the SCO to its source process. Empty / missing
        // memo is a programming error -- fail loudly rather than silently writing
        // an unidentifiable SCO into NetSuite.
        if (typeof memo !== "string" || memo.trim().length === 0) {
            throw new Error("[terminateSubscription] 'memo' is required and must be a non-empty string. " +
                "Pass a stable, source-identifying memo (e.g. \"Subscription Transfer for <subId>\").");
        }
        // Extract date part if endDate includes time component (e.g., "2026-03-01T00:00:00.000Z" -> "2026-03-01")
        // NetSuite API expects YYYY-MM-DD format for effectiveDate field
        let effectiveDate = endDate;
        if (endDate.includes('T')) {
            effectiveDate = endDate.split('T')[0];
        }
        // If date is in NetSuite format (D-Mon-YYYY), convert to YYYY-MM-DD
        if (/^\d{1,2}-[A-Za-z]{3}-\d{4}$/.test(effectiveDate)) {
            const [day, month, year] = effectiveDate.split('-');
            const monthMap = {
                'Jan': '01', 'Feb': '02', 'Mar': '03', 'Apr': '04',
                'May': '05', 'Jun': '06', 'Jul': '07', 'Aug': '08',
                'Sep': '09', 'Oct': '10', 'Nov': '11', 'Dec': '12'
            };
            const monthNumber = monthMap[month];
            if (monthNumber) {
                effectiveDate = `${year}-${monthNumber}-${day.padStart(2, '0')}`;
            }
        }
        console.log(`End Date (input): ${endDate}`);
        console.log(`End Date (YYYY-MM-DD format): ${effectiveDate}`);
        const payload = {
            action: { id: "TERMINATE" },
            approvalStatus: { id: "APPROVED" },
            customer: { id: customerId },
            billingAccount: { id: billingAccountId },
            subscription: { id: subscriptionId },
            subline: {
                items: itemNumbers.map((itemNumber) => ({
                    apply: true,
                    sequence: itemNumber,
                })),
            },
            effectiveDate: effectiveDate,
            custrecord_st_change_reason: {
                id: 4,
            },
            requestOffCycleInvoice: true,
            terminateAtStartOfDay: true,
            memo: memo,
        };
        try {
            const response = await client.post(`/services/rest/record/v1/subscriptionChangeOrder`, payload);
            if (response.status === 204) {
                console.log("Subscription terminated successfully.");
                return true;
            }
            else {
                console.log("Failed to terminate subscription:", response);
                return false;
            }
        }
        catch (error) {
            console.error("Error terminating subscription:", error);
            return false;
        }
    };
    const createSubscriptionChangeOrder = async (customerId, billingAccountId, subscriptionId, itemNumbers, startDate, negativeValue, refNumber) => {
        // Extract date part if startDate includes time component (e.g., "2025-10-01T00:00:00.000Z" -> "2025-10-01")
        // NetSuite API expects YYYY-MM-DD format for effectiveDate field
        let effectiveDate = startDate;
        if (startDate.includes('T')) {
            effectiveDate = startDate.split('T')[0];
        }
        // If date is in NetSuite format (D-Mon-YYYY), convert to YYYY-MM-DD
        if (/^\d{1,2}-[A-Za-z]{3}-\d{4}$/.test(effectiveDate)) {
            const [day, month, year] = effectiveDate.split('-');
            const monthMap = {
                'Jan': '01', 'Feb': '02', 'Mar': '03', 'Apr': '04',
                'May': '05', 'Jun': '06', 'Jul': '07', 'Aug': '08',
                'Sep': '09', 'Oct': '10', 'Nov': '11', 'Dec': '12'
            };
            const monthNumber = monthMap[month];
            if (monthNumber) {
                effectiveDate = `${year}-${monthNumber}-${day.padStart(2, '0')}`;
            }
        }
        console.log(`[createSubscriptionChangeOrder] Start Date (input): ${startDate}`);
        console.log(`[createSubscriptionChangeOrder] Start Date (YYYY-MM-DD format): ${effectiveDate}`);
        const payload = {
            action: { id: "ACTIVATE" },
            approvalStatus: { id: "APPROVED" },
            modificationType: { id: "NEW_CHURN" },
            customer: { id: customerId },
            billingAccount: { id: billingAccountId },
            subscription: { id: subscriptionId },
            subline: {
                items: itemNumbers.map((itemNumber) => ({
                    apply: true,
                    sequence: itemNumber,
                })),
            },
            effectiveDate: effectiveDate,
            custrecord_st_change_reason: {
                id: 4,
            },
            totalintervalvalue: negativeValue,
            memo: refNumber,
        };
        try {
            const response = await client.post("/services/rest/record/v1/subscriptionChangeOrder", payload);
            if (response.status === 204 || response.status === 200 || response.status === 201) {
                console.log("Subscription activated successfully.");
                return true;
            }
            else {
                console.log("Failed to activate subscription:", response);
                return false;
            }
        }
        catch (error) {
            console.error("Error activating subscription:", error);
            return false;
        }
    };
    const createInvoice = async (customerId, billingAccountId, startDate, endDate, asOfDate, customFormId, memo, paymentTerms, otherRefNum) => {
        try {
            // Convert all dates to YYYY-MM-DD format for NetSuite API
            const asOfDateFormatted = convertToYYYYMMDD(asOfDate);
            const startDateFormatted = convertToYYYYMMDD(startDate);
            const endDateFormatted = convertToYYYYMMDD(endDate);
            const tranDateFormatted = convertToYYYYMMDD(new Date());
            console.log(`[createInvoice] Converting dates to YYYY-MM-DD format:`);
            console.log(`  asOfDate: ${asOfDate} → ${asOfDateFormatted}`);
            console.log(`  startDate: ${startDate} → ${startDateFormatted}`);
            console.log(`  endDate: ${endDate} → ${endDateFormatted}`);
            console.log(`  tranDate: ${new Date().toISOString().slice(0, 10)} → ${tranDateFormatted}`);
            const payload = {
                entity: { id: customerId },
                custbody_end_user_new: { id: customerId },
                billingAccount: { id: billingAccountId },
                whichChargesToAdd: {
                    id: "AS_OF_DATE",
                },
                asOfDate: asOfDateFormatted,
                tranDate: tranDateFormatted,
                startDate: startDateFormatted,
                endDate: endDateFormatted,
                customForm: {
                    id: customFormId,
                },
                terms: {
                    id: paymentTerms,
                },
                memo: memo,
                otherRefNum,
                taxdetailsoverride: client.isProd ? undefined : true,
            };
            const response = await client.post("/services/rest/record/v1/invoice", payload);
            if (response.status === 204) {
                console.log("Invoice created successfully:", response.id);
                return response.id;
            }
            else {
                console.log("Failed to create invoice:", response);
                return null;
            }
        }
        catch (error) {
            console.error("Error creating invoice:", error);
            return null;
        }
    };
    const updateInvoiceMetadata = async (invoiceId, startDate, endDate, otherRefNum) => {
        try {
            // Convert dates to YYYY-MM-DD format for NetSuite API
            const startDateFormatted = convertToYYYYMMDD(startDate);
            const endDateFormatted = convertToYYYYMMDD(endDate);
            const response = await client.patch(`/services/rest/record/v1/invoice/${invoiceId}`, {
                startDate: startDateFormatted,
                endDate: endDateFormatted,
                otherRefNum,
            });
            if (response.status === 204) {
                console.log("Invoice metadata updated successfully.");
            }
            else {
                console.log("Failed to update invoice metadata:", response);
            }
        }
        catch (error) {
            console.error("Error updating invoice metadata:", error);
        }
    };
    const updateInvoiceDescription = async (invoiceId, description) => {
        const payload = {
            description,
        };
        try {
            // must be first item on description
            const response = await client.patch(`/services/rest/record/v1/invoice/${invoiceId}/item/1`, payload);
            if (response.status === 204) {
                console.log("Invoice description updated successfully.");
            }
            else {
                console.log("Failed to update invoice description:", response);
            }
        }
        catch (error) {
            console.error("Error updating invoice description:", error);
        }
    };
    const updateInvoiceSubscriptionPlan = async (invoiceId, subscriptionPlanId) => {
        const payload = {
            custcol_inv_subs_plan: { id: subscriptionPlanId },
        };
        try {
            const response = await client.patch(`/services/rest/record/v1/invoice/${invoiceId}/item/1`, payload);
            if (response.status === 204) {
                console.log("Invoice subscription plan updated successfully.");
            }
            else {
                console.log("Failed to update invoice subscription plan:", response);
            }
        }
        catch (error) {
            console.error("Error updating invoice subscription plan:", error);
        }
    };
    const updateInvoiceItemDates = async (invoiceId, startDate, endDate) => {
        // Convert dates to YYYY-MM-DD format for NetSuite API
        // Note: Custom fields may accept different formats, but YYYY-MM-DD is safest
        const startDateFormatted = convertToYYYYMMDD(startDate);
        const endDateFormatted = convertToYYYYMMDD(endDate);
        const payload = {
            custcol_swe_contract_end_date: endDateFormatted,
            custcol_swe_contract_start_date: startDateFormatted,
        };
        try {
            const response = await fetchData(`/services/rest/record/v1/invoice/${invoiceId}/item`);
            const itemLinks = response.items.map((item) => item.links.find((link) => link.rel === "self")?.href);
            const itemIds = itemLinks.map((link) => link.split(".com")[1]);
            const responses = [];
            for (const itemId of itemIds) {
                // Throttle by 1 second
                await new Promise((resolve) => setTimeout(resolve, 1000));
                const itemResponse = await client.patch(itemId, payload);
                responses.push(itemResponse);
            }
            if (responses.every((resp) => resp.status === 204)) {
                console.log("Invoice item dates updated successfully.");
            }
            else {
                console.log("Failed to update invoice item dates:", response);
            }
        }
        catch (error) {
            console.error("Error updating invoice item dates:", error);
        }
    };
    const uploadFileAndAttachToRecord = async (restletId, deployId, folderId, file, record) => {
        try {
            const response = await client.restlet(restletId, deployId, "POST", {
                folderId,
                fileData: file.fileData,
                fileName: file.fileName,
                fileType: file.fileType,
                recordType: record.recordType,
                recordId: record.recordId,
            });
            if (response.status === 200) {
                const data = await response.json();
                if (data.error) {
                    console.log("Failed to upload file:", data.error);
                    return null;
                }
            }
        }
        catch (error) {
            console.error("Error uploading file:", error);
            return null;
        }
    };
    const sendInvoice = async (restletId, deployId, invoiceId, recipientEmail, additionalRecipients, bcc, emailTemplateId) => {
        const payload = {
            invoiceId,
            recipientEmail,
            additionalRecipients,
            bcc,
            emailTemplateId,
        };
        try {
            const response = await client.restlet(restletId, deployId, "POST", payload);
            if (response.status === 200) {
                const data = await response.json();
                console.log("Response:", data);
                if (!data.success) {
                    console.log("Failed to send invoice:", data.error);
                }
            }
        }
        catch (error) {
            console.error("Error sending invoice:", error);
        }
    };
    const getCustomerPreferences = async (customerId) => {
        return fetchData(`/services/rest/record/v1/customer/${customerId}`);
    };
    const updateCustomerPreferences = async (customerId, payload) => {
        const response = await client.patch(`/services/rest/record/v1/customer/${customerId}`, payload);
        if (response.status >= 200 && response.status < 300) {
            console.log(`Customer ${customerId} preferences updated successfully.`);
            return true;
        }
        console.log(`Failed to update customer ${customerId} preferences:`, response);
        return false;
    };
    const createCreditMemo = async (payload) => {
        const response = await client.post(`/services/rest/record/v1/creditMemo`, payload);
        if (response.status === 204) {
            console.log("Credit Memo created successfully.");
            const creditmemo_id = response?.id;
            return creditmemo_id;
        }
        else {
            console.log("Failed to create credit memo:", response);
            return null;
        }
    };
    const updateCreditMemoTranId = async (creditMemoId, payload) => {
        const response = await client.patch(`/services/rest/record/v1/creditMemo/${creditMemoId}`, payload);
        if (response.status === 204) {
            console.log(`Credit Memo ${creditMemoId} updated successfully.`);
        }
        else {
            console.log(`Failed to update credit memo ${creditMemoId}:`, response);
            throw new Error(`Failed to update credit memo ${creditMemoId}: ${response}`);
        }
    };
    const addCustomerSusidiaryRelationship = async (payload) => {
        return await client.post(`/services/rest/record/v1/customerSubsidiaryRelationship`, payload);
    };
    const createNewSubscription = async (payload) => {
        const response = await client.post(`/services/rest/record/v1/subscription`, payload);
        if (response.status === 204) {
            console.log("New Subscription created successfully.");
            return response.id;
        }
        else if (response.status === 200 || response.status === 201) {
            console.log("New Subscription created successfully.");
            const id = response.headers.get("Location")?.split("/").pop();
            return id;
        }
        else {
            return null;
        }
    };
    const updatePricePlanValue = async (pricePlanId, value) => {
        const payload = {
            value: value
        };
        const response = await client.patch(`/services/rest/record/v1/pricePlan/${pricePlanId}/priceTiers/0`, payload);
        if (response.status === 204) {
            console.log(`Price Plan ${pricePlanId} updated successfully.`);
        }
        else {
            console.log(`Failed to update price plan ${pricePlanId}:`, response);
            throw new Error(`Failed to update price plan ${pricePlanId}: ${response}`);
        }
    };
    const updateLineItemStatus = async (newSubscriptionId, lineNumber, status) => {
        const payload = {
            status: {
                refName: status
            }
        };
        const response = await client.patch(`/services/rest/record/v1/subscription/${newSubscriptionId}/subscriptionline/${lineNumber}`, payload);
        if (response.status === 204) {
            console.log(`Line Item ${lineNumber} updated successfully.`);
        }
        else {
            console.log(`Failed to update line item ${lineNumber}:`, response);
            throw new Error(`Failed to update line item ${lineNumber}: ${response}`);
        }
    };
    /**
     * Remove invoice line items via NetSuite Restlet
     * Uses retry logic with configurable attempts and delay
     * @param invoiceId The invoice ID to remove items from
     * @param itemIdsToRemove Array of composite line IDs in format {invoiceId}_{lineNumber}
     * @param restletScriptId Optional script ID (defaults to env var or "3219")
     * @param restletDeploymentId Optional deployment ID (defaults to env var or "1")
     */
    const removeInvoiceItems = async (invoiceId, itemIdsToRemove, restletScriptId, restletDeploymentId) => {
        const maxRetries = 10;
        const retryDelayMs = 70000; // 70 seconds
        const scriptId = restletScriptId || process.env.REMOVE_INV_ITEMS_RESTLET_ID || "3219";
        const deploymentId = restletDeploymentId || process.env.REMOVE_INV_ITEMS_RESTLET_DEPLOY_ID || "1";
        if (!scriptId || !deploymentId) {
            throw new Error("Restlet Script ID or Deployment ID not configured for invoice item removal.");
        }
        const payload = {
            invoiceId,
            itemLineIds: itemIdsToRemove,
        };
        for (let attempt = 1; attempt <= maxRetries; attempt++) {
            try {
                console.log(`[removeInvoiceItems] Calling Restlet to remove line IDs [${itemIdsToRemove.join(', ')}] from invoice ${invoiceId} (attempt ${attempt}/${maxRetries})...`);
                const response = await client.restlet(scriptId, deploymentId, "POST", payload);
                // Log full response for debugging
                console.log(`=== RESTLET RESPONSE START ===`);
                console.log(`Invoice ID: ${invoiceId}`);
                console.log(`Requested removal of line IDs: [${itemIdsToRemove.join(', ')}]`);
                console.log(`Success: ${response.success}`);
                console.log(`Lines Before Count: ${response.linesBeforeCount}`);
                console.log(`Lines After Count: ${response.linesAfterCount}`);
                console.log(`All Line IDs Before: [${response.allLineIdsBefore?.join(', ') || 'N/A'}]`);
                console.log(`All Line IDs After: [${response.allLineIdsAfter?.join(', ') || 'N/A'}]`);
                console.log(`Removed Lines: [${response.removedLines?.join(', ') || 'none'}]`);
                console.log(`Lines Not Found: [${response.linesNotFound?.join(', ') || 'none'}]`);
                if (response.error) {
                    console.log(`Error: ${response.error}`);
                }
                console.log(`=== RESTLET RESPONSE END ===`);
                // Handle error/success in response from restlet
                if (!response.success) {
                    throw new Error(response.error || "Unknown error from NetSuite Restlet.");
                }
                console.log(`[removeInvoiceItems] Restlet completed: Removed [${response.removedLines?.join(', ') || 'none'}], Not found [${response.linesNotFound?.join(', ') || 'none'}]`);
                return; // Success - exit the retry loop
            }
            catch (error) {
                console.error(`[removeInvoiceItems] Failed (attempt ${attempt}/${maxRetries}):`, error);
                if (attempt < maxRetries) {
                    console.log(`[removeInvoiceItems] Retrying after ${retryDelayMs}ms...`);
                    await new Promise(resolve => setTimeout(resolve, retryDelayMs));
                }
                else {
                    console.error("[removeInvoiceItems] Error removing invoice items via Restlet after all retries:", error);
                    throw error;
                }
            }
        }
    };
    return {
        fetchData,
        postData,
        executeSuiteQL,
        getCustomer,
        getCustomerAddresses,
        getClass,
        getSubscriptionPlan,
        getCustomerSubsidiaries,
        addSubsidiaryToCustomer,
        addAddressToCustomer,
        createBillingAccount,
        createPricePlan,
        createSubscription,
        getSubscriptionPlanLines,
        getSubscriptionLines,
        getSubscriptionDetails,
        updateSubscriptionLineStatus,
        getBillingAccount,
        updateBillingAccountAddress,
        copyBillingAccountAddressesFromOld,
        createContractualDocument,
        linkContractualDocument,
        activateSubscription,
        createInvoice,
        updateInvoiceMetadata,
        updateInvoiceSubscriptionPlan,
        updateInvoiceDescription,
        turnOffAutoRenewal,
        updateInvoiceItemDates,
        uploadFileAndAttachToRecord,
        sendInvoice,
        getPriceIntervals,
        getPriceIntervalDetails,
        createNewPricePlan,
        getMaxSubscriptionLineNumber,
        fetchPriceIntervals,
        updateSubscription,
        fetchNonSaleInventoryItems,
        createSubscriptionChangeOrder,
        terminateSubscription,
        getCustomerPreferences,
        updateCustomerPreferences,
        createCreditMemo,
        updateCreditMemoTranId,
        getSubscriptionLinesNew,
        addCustomerSusidiaryRelationship,
        createNewSubscription,
        updatePricePlanValue,
        updateLineItemStatus,
        patchData,
        deleteData,
        removeInvoiceItems,
        isProd: client.isProd
    };
};
exports.createNetSuiteUtils = createNetSuiteUtils;
